import {
    Thermometer,
    Zap,
    Factory,
    BarChart3,
    ChevronRight,
    Settings,
    Droplets,
    RefreshCw,
    Clock,
    CircleDollarSign,
    ArrowUpRight
} from 'lucide-react';

export default function HeatPumpsDetailed() {
    const industries = [
        { name: 'Minería', desc: 'Campamentos (ACS para duchas/casinos) y procesos de lixiviación (hidrometalurgia).', icon: <Factory className="w-6 h-6" /> },
        { name: 'Acuicultura', desc: 'Control de temperatura en estanques de salmones (Pisciculturas) y procesamiento.', icon: <Droplets className="w-6 h-6" /> },
        { name: 'Forestal / Celulosa', desc: 'Secado de madera controlado (sustitución de calderas de biomasa/gas).', icon: <Settings className="w-6 h-6" /> },
        { name: 'Agroindustria', desc: 'Invernaderos, secado de granos y control térmico en vitivinicultura.', icon: <Zap className="w-6 h-6" /> },
        { name: 'Alimentaria', desc: 'Lácteas (pasteurización), cecinas (escaldado) y limpieza industrial (CIP).', icon: <BarChart3 className="w-6 h-6" /> },
        { name: 'Hospitalidad/Salud', desc: 'Agua Caliente Sanitaria (ACS) y climatización de alta eficiencia.', icon: <Thermometer className="w-6 h-6" /> },
    ];

    const businessModels = [
        {
            title: 'Venta + Mantenimiento (CAPEX)',
            desc: 'El cliente adquiere equipos y recibe servicio técnico/eléctrico integral con contratos de 7-10 años.',
            icon: <CircleDollarSign className="w-8 h-8" />,
            highlight: false
        },
        {
            title: 'Calor como Servicio (OPEX)',
            desc: 'Instalación sin costo inicial. Se paga una tarifa mensual por el agua caliente con ahorros garantizados.',
            icon: <Clock className="w-8 h-8" />,
            highlight: true
        },
        {
            title: 'Retrofitting & Modernización',
            desc: 'Actualización de salas térmicas existentes añadiendo módulos de ahorro y cumplimiento ambiental.',
            icon: <RefreshCw className="w-8 h-8" />,
            highlight: false
        }
    ];

    return (
        <section id="bombas-calor" className="py-32 bg-white overflow-hidden relative">
            <div className="section-container relative z-10">
                {/* Intro Section - The "Why" and "What" */}
                <div className="grid lg:grid-cols-12 gap-16 items-start mb-40">
                    <div className="lg:col-span-7" data-aos="fade-right">
                        <div className="badge-premium mb-8 bg-accent/10 text-accent border-accent/20">
                            <Zap size={14} />
                            <span>La Revolución Térmica</span>
                        </div>
                        <h2 className="text-4xl md:text-6xl font-black text-primary-dark mb-10 leading-[1.1] tracking-tighter">
                            Ingeniería de <br />
                            <span className="text-accent italic">Eficiencia Extrema</span>
                        </h2>
                        <p className="text-xl text-slate-600 mb-12 leading-relaxed font-light">
                            Las Bombas de Calor representan el salto tecnológico más importante en la descarbonización industrial. No generan calor; lo <span className="font-bold text-primary-dark underline decoration-accent/20 underline-offset-4">mueven de forma inteligente</span>, entregando hasta 5 veces más energía de la que consumen.
                        </p>

                        <div className="grid sm:grid-cols-2 gap-8 mb-16">
                            <div className="p-8 rounded-3xl bg-slate-50 border border-slate-100 shadow-sm relative group overflow-hidden">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-accent/5 rounded-bl-full pointer-events-none" />
                                <span className="text-[10px] font-bold uppercase tracking-widest text-accent mb-4 block">Rendimiento Técnico</span>
                                <h3 className="text-4xl font-black text-primary-dark mb-2 tracking-tighter">COP 5.0</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">Por cada 1 kWh de entrada, generamos 5 kWh de potencia térmica neta.</p>
                            </div>
                            <div className="p-8 rounded-3xl bg-slate-50 border border-slate-100 shadow-sm relative group overflow-hidden">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-bl-full pointer-events-none" />
                                <span className="text-[10px] font-bold uppercase tracking-widest text-energy-blue mb-4 block">Balance Energético</span>
                                <h3 className="text-4xl font-black text-primary-dark mb-2 tracking-tighter">80% Ahorro</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">Reducción típica frente a sistemas basados en combustión tradicional.</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {[
                                { title: 'Estrategia de Calor Residual', desc: 'Elevamos el COP hasta 5.5 mediante la captura de calor desperdiciado de motores.' },
                                { title: 'Gestión Inteligente de ACS', desc: 'Control centralizado para campamentos mineros y centros de salud.' },
                                { title: 'Continuidad Garantizada', desc: 'Configuraciones redundantes con respaldo automático.' }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-6 p-6 rounded-[2rem] bg-white hover:shadow-premium transition-all duration-300 border border-slate-100 group">
                                    <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                                        <ArrowUpRight size={24} />
                                    </div>
                                    <div>
                                        <h5 className="font-black text-primary-dark mb-1">{item.title}</h5>
                                        <p className="text-sm text-slate-500 font-light">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5 sticky top-32 self-start" data-aos="fade-left">
                        <div className="relative">
                            <div className="absolute -inset-10 bg-energy-blue/5 rounded-full blur-[100px] -z-10" />
                            <div className="bg-white border border-slate-100 p-6 rounded-[3rem] shadow-premium relative z-10 overflow-hidden">
                                <div className="bg-slate-50 aspect-square rounded-[2rem] overflow-hidden flex items-center justify-center p-12 relative group shadow-inner">
                                    <img
                                        src={`${import.meta.env.BASE_URL}images/heat_pump_schematic_light.png`}
                                        alt="Esquema Bomba de Calor Industrial"
                                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <div className="absolute top-6 left-6 badge-premium bg-white shadow-sm border-slate-100">
                                        <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                                        <span>Live Monitoring</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Industries Grid */}
                <div className="mb-40">
                    <div className="text-center mb-20">
                        <h2 className="text-4xl md:text-5xl font-black text-primary-dark mb-6 tracking-tighter">Versatilidad <span className="text-slate-400">Multisectorial</span></h2>
                        <p className="text-slate-500 font-light max-w-2xl mx-auto">Nuestra tecnología se adapta a los procesos más exigentes de la industria chilena, garantizando estabilidad operativa.</p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {industries.map((ind, i) => (
                            <div key={i} className="group p-10 rounded-[2.5rem] bg-white border border-slate-100 hover:border-accent/30 hover:shadow-premium transition-all duration-500">
                                <div className="w-16 h-16 rounded-2xl bg-slate-50 text-slate-400 group-hover:bg-accent group-hover:text-white flex items-center justify-center mb-8 transition-all duration-300">
                                    {ind.icon}
                                </div>
                                <h3 className="text-2xl font-black text-primary-dark mb-4 tracking-tight">{ind.name}</h3>
                                <p className="text-slate-500 font-light text-sm leading-relaxed">
                                    {ind.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Business Models Section */}
                <div className="relative rounded-[4rem] overflow-hidden shadow-premium group">
                    <div className="absolute inset-0 bg-primary-dark overflow-hidden">
                        <div className="absolute inset-0 bg-mesh-gradient opacity-30 group-hover:opacity-50 transition-opacity" />
                    </div>

                    <div className="relative z-10 p-12 lg:p-24 text-white">
                        <div className="text-center mb-20">
                            <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tighter underline decoration-accent/20 underline-offset-8 italic">Modelos de Inversión</h2>
                            <p className="text-white/60 font-light max-w-xl mx-auto">Diseñamos la estructura financiera que mejor se adapte a su planificación presupuestaria.</p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {businessModels.map((model, i) => (
                                <div key={i} className={`p-10 rounded-[3rem] transition-all duration-500 border
                                    ${model.highlight
                                        ? 'bg-white/10 border-white/20 backdrop-blur-md shadow-2xl scale-105 z-20'
                                        : 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10'}
                                `}>
                                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-8 shadow-lg
                                        ${model.highlight ? 'bg-accent text-white' : 'bg-white/10 text-accent'}
                                    `}>
                                        {model.icon}
                                    </div>
                                    <h3 className="text-2xl font-black mb-6 tracking-tight leading-none">{model.title}</h3>
                                    <p className="text-white/60 font-light text-sm leading-relaxed mb-8">
                                        {model.desc}
                                    </p>
                                    {model.highlight && (
                                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent font-bold text-[10px] uppercase tracking-widest border border-accent/20">
                                            Recomendado por Ahorro
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="mt-20 text-center">
                            <button className="btn-accent px-12 py-5 text-xl mx-auto shadow-2xl shadow-accent/20 hover:scale-105 active:scale-95">
                                Estudio de Factibilidad Sin Costo
                                <ChevronRight />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

